import { useState, useEffect } from 'react';
import { X, RefreshCw, Search, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { API_URL } from '../config';

interface Service {
  id: string;
  name: string;
  category: string;
  icon: string;
  color: string;
  description: string;
  keywords: string[];
  enabled: boolean;
}

interface ServicesPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerateFlow: (flowData: any) => void;
}

export default function ServicesPanel({ isOpen, onClose, onGenerateFlow }: ServicesPanelProps) {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory] = useState<string | null>(null);
  const [_categories, setCategories] = useState<string[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) {
      loadServices();
    }
  }, [isOpen]);

  const loadServices = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/services/list`);
      const data = await response.json();
      
      if (data.success) {
        setServices(data.services);
        setCategories(data.categories);
        
        // Expand all categories by default
        setExpandedCategories(new Set(data.categories));
      }
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateFlow = async (serviceId: string) => {
    try {
      const response = await fetch(`${API_URL}/services/generate-flow/${serviceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start_position: { x: 100, y: 100 } })
      });
      
      const data = await response.json();
      
      if (data.success && data.flow) {
        onGenerateFlow(data.flow);
        onClose();
      }
    } catch (error) {
      console.error('Error generating flow:', error);
      alert('❌ Errore nella generazione del flow');
    }
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const filteredServices = services.filter(service => {
    const matchesSearch = searchQuery === '' || 
      service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.keywords.some(kw => kw.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = !selectedCategory || service.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const servicesByCategory = filteredServices.reduce((acc, service) => {
    if (!acc[service.category]) {
      acc[service.category] = [];
    }
    acc[service.category].push(service);
    return acc;
  }, {} as Record<string, Service[]>);

  const categoryColors: Record<string, string> = {
    astrology: 'bg-purple-100 text-purple-800 border-purple-300',
    weather: 'bg-blue-100 text-blue-800 border-blue-300',
    system: 'bg-green-100 text-green-800 border-green-300',
    examples: 'bg-gray-100 text-gray-800 border-gray-300',
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b-4 border-teal-300 bg-gradient-to-r from-teal-50 to-cyan-50">
          <div>
            <h2 className="text-2xl font-bold text-teal-900 flex items-center gap-2">
              <span>🔌</span>
              Servizi / API
            </h2>
            <p className="text-sm text-teal-700 mt-1">
              Genera automaticamente flow da servizi pre-configurati
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadServices}
              className="p-2 hover:bg-teal-100 rounded-lg transition-colors"
              title="Ricarica servizi"
            >
              <RefreshCw className="w-5 h-5 text-teal-700" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-red-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-red-600" />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b-2 border-gray-200 bg-gray-50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca servizi..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Services List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-gray-600 mt-4">Caricamento servizi...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(servicesByCategory).length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-lg">Nessun servizio trovato</p>
                  <p className="text-sm mt-2">Prova con una ricerca diversa</p>
                </div>
              ) : (
                Object.entries(servicesByCategory).map(([category, categoryServices]) => (
                  <div key={category} className="bg-gray-50 rounded-xl overflow-hidden">
                    {/* Category Header */}
                    <button
                      onClick={() => toggleCategory(category)}
                      className={`w-full px-4 py-3 flex items-center justify-between ${
                        categoryColors[category] || 'bg-gray-100 text-gray-800 border-gray-300'
                      } border-2 hover:opacity-80 transition-opacity`}
                    >
                      <div className="flex items-center gap-2">
                        {expandedCategories.has(category) ? (
                          <ChevronDown className="w-5 h-5" />
                        ) : (
                          <ChevronRight className="w-5 h-5" />
                        )}
                        <span className="font-bold capitalize">{category}</span>
                        <span className="text-xs bg-white bg-opacity-50 px-2 py-1 rounded-full">
                          {categoryServices.length}
                        </span>
                      </div>
                    </button>

                    {/* Services in Category */}
                    {expandedCategories.has(category) && (
                      <div className="p-2 space-y-2">
                        {categoryServices.map(service => (
                          <div
                            key={service.id}
                            className="bg-white rounded-xl border-2 border-gray-200 hover:border-teal-400 hover:shadow-lg transition-all p-4"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <span className="text-3xl">{service.icon}</span>
                                  <div>
                                    <h3 className="font-bold text-lg text-gray-900">{service.name}</h3>
                                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                      {service.description}
                                    </p>
                                  </div>
                                </div>
                                
                                {/* Keywords */}
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {service.keywords.slice(0, 4).map(keyword => (
                                    <span
                                      key={keyword}
                                      className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full"
                                    >
                                      {keyword}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              {/* Generate Button */}
                              <button
                                onClick={() => handleGenerateFlow(service.id)}
                                className="ml-4 px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl font-semibold hover:from-teal-600 hover:to-cyan-600 transition-all shadow-lg hover:shadow-xl flex items-center gap-2 whitespace-nowrap"
                                title="Genera flow automaticamente"
                              >
                                <Sparkles className="w-4 h-4" />
                                Genera Flow
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t-2 border-gray-200 bg-gray-50 text-center text-sm text-gray-600">
          {services.length} servizi disponibili • 
          <a
            href={`${API_URL}/docs#/Services`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-teal-600 hover:underline ml-1"
          >
            API Docs
          </a>
        </div>
      </div>
    </div>
  );
}

